import { Component, Input, Output, EventEmitter, OnInit, ViewChild, ApplicationRef, Renderer2 } from '@angular/core';
import { XMLService } from '../../../services/xml/xml.service';
import { ContextMenuOption } from '../../../models/context-menu-option';
import { UntypedFormGroup, UntypedFormArray, AbstractControl } from '@angular/forms';
import { CommonService } from '../../../services/common/common.service';
import { MessageType, SearchType } from '../../../models/enums';
import { WorkflowStep } from '../../../models/workflow';
import { WorkflowQueryModalComponent } from '../../modals/workflow-query-modal/workflow-query-modal.component';
import { RestService } from '../../../services/rest/rest.service';
import { Permissions, ObjectActionListType, ActionType } from '../../../models/permission';
import { AdvancedSearchBundle } from '../../search/search.component';
import { Task } from '../../../models/task';
import { GenericInputComponent } from '../../generic-input/generic-input.component';
import { ContextMenuComponent } from '../../../components/context-menu/context-menu.component';
import { MAX_LIMIT_OF_RENDERED_QUERY } from '../../../models/constants';

@Component({
    standalone: false,
    selector: 'res-workflow-step',
    templateUrl: './workflow-step.component.html',
    styleUrls: ['./workflow-step.component.scss']
})
export class WorkflowStepComponent implements OnInit {
    static emptyQuery = 'No query';
    @Input() workflowForm: UntypedFormGroup;
    @Input() task: Task;
    @Input() workflowStep: WorkflowStep;
    @Input() stepNumber: number;
    @Input() locked: boolean;
    @Input() running: boolean;
    @Input() validityResult: string;
    @ViewChild('editMenu', { static: true }) contextMenu: ContextMenuComponent;
    @ViewChild('input') input: GenericInputComponent;
    isMouseOver = false;
    hoverIcon = false;
    selectedQuery: any;
    selectedQueryDisplayValue: string = '';
    colorizedQuery: string;
    advancedQuery: AdvancedSearchBundle;
    hasCreatePermissions: boolean;
    isDateQuery = false;
    resultCount = undefined;
    metadata: boolean;
    families: boolean;
    synonyms: boolean;
    generateReports: boolean;
    searchType: string;
    menuOptions = [
        new ContextMenuOption({
            name: 'Add Step Before',
            iconClass: 'fa-plus',
            iconColor: 'color-green-04',
            click: (item: any) => this.addStep(this.stepNumber)
        }),
        new ContextMenuOption({
            name: 'Add Step After',
            iconClass: 'fa-plus',
            iconColor: 'color-green-04',
            click: (item: any) => this.addStep(this.stepNumber + 1)
        }),
        new ContextMenuOption({
            divider: true
        }),
        new ContextMenuOption({
            name: 'Move Up',
            iconClass: 'fa-arrow-up',
            iconColor: 'color-blue-01',
            enabled: (item: any) => this.stepNumber !== 0,
            click: (item: any) => this.moveUp(this.stepNumber)
        }),
        new ContextMenuOption({
            name: 'Move Down',
            iconClass: 'fa-arrow-down',
            iconColor: 'color-blue-01',
            enabled: (item: any) => {
                return (this.workflowForm && this.stepNumber !== (this.workflowForm.value.queryItems.length - 1));
            },
            click: (item: any) => this.moveUp(this.stepNumber + 1)
        }),
        new ContextMenuOption({
            divider: true
        }),
        new ContextMenuOption({
            name: 'Edit ...',
            iconClass: 'fa-pencil',
            iconColor: 'color-blue-01',
            visible: (item: any) => item.source?.type === 'contextmenu',
            click: (item: any) => this.edit()
        }),
        new ContextMenuOption({
            name: 'Clear Query',
            iconClass: 'fa-times',
            iconColor: 'color-grey-10',
            enabled: (item: any) => this.step.get('query').value,
            click: (item: any) => this.clearQuery()
        }),
        new ContextMenuOption({
            name: 'Delete',
            iconClass: 'fa-trash',
            iconColor: 'color-red-05',
            enabled: true,
            click: (item: any) => this.removeStep(this.stepNumber)
        })
    ];
    @Input() addStep: (index: number) => void;
    @Input() removeStep: (index: number) => void;
    @Input() moveUp: (index: number) => void;
    @Output() stepChange = new EventEmitter<any>();

    get step(): AbstractControl {
        return (this.workflowForm.get('queryItems') as UntypedFormArray).at(this.stepNumber);
    }

    get queryType(): string {
        return this.isDateQuery ? 'Date Range Query' : 'Term Query';
    }

    constructor(private xmlService: XMLService, private renderer: Renderer2,
        private commonService: CommonService, private restService: RestService, private applicationRef: ApplicationRef) { }

    ngOnInit(): void {
        this.hasCreatePermissions = Permissions.checkUserPermission(ObjectActionListType.WORK_FLOW, ActionType.CREATE);
        if ((!this.step.value.query || this.step.value.query.length === 0) && this.selectedQuery !== 'No query') {
            this.selectedQuery = WorkflowStepComponent.emptyQuery;
            this.selectedQueryDisplayValue = WorkflowStepComponent.emptyQuery;
        } else {
            this.selectedQuery = this.xmlService.xmlToQueryConverter(this.step.value.query);
            this.setQueryDisplayValue(this.selectedQuery);
        }
        if (this.workflowStep && this.workflowStep.attributes &&
            this.workflowStep.attributes.filter(attr => attr.name === 'noOfResults').length > 0 && this.selectedQuery !== 'No query') {
            this.resultCount = this.workflowStep.attributes.filter(attr => attr.name === 'noOfResults')[0].value;
        }
        this.isDateQuery = this.xmlService.isDateQuery(this.step.value.query);
        this.setOptions();
        this.step.patchValue({ isDateQuery: this.isDateQuery });
        setTimeout(() => { this.input?.highlightNOW(this.selectedQueryDisplayValue, false, false, true); });
    }

    setOptions(): void {
        this.metadata = this.step.value.expandFields;
        this.families = this.step.value.includeFamilyMembers;
        this.synonyms = this.step.value.includeSynonyms;
        this.generateReports = this.step.value.reportRequests?.length > 0;
        const xmlObj = this.xmlService.xmlToQueryConverter(this.step.value.query, true);
        if (xmlObj && (xmlObj as any).searchType === 'DRAdvancedSearch') {
            this.searchType = SearchType.ADVANCED;
        } else {
            this.searchType = SearchType.FREEFORM;
        }
    }

    navigateToDocList(): void {
        this.commonService.sendMessage(MessageType.navigationRequest, { handle: this.workflowStep.responseHandle });
    }

    edit(): void {
        const xmlObj = this.xmlService.xmlToQueryConverter(this.step.value.query, true);
        if (xmlObj && (xmlObj as any).searchType === 'DRAdvancedSearch') {
            this.step.value.searchType = SearchType.ADVANCED;
        } else {
            this.step.value.searchType = SearchType.FREEFORM;
        }
        this.commonService.openModal(WorkflowQueryModalComponent, {
            class: 'vertical-percent-100 horizontal-percent-100',
            modalData: {
                step: {
                    searchType: this.step.value.searchType,
                    query: this.step.value.query,
                    includeFamilyMembers: this.step.value.includeFamilyMembers,
                    expandFields: this.step.value.expandFields,
                    includeSynonyms: this.step.value.includeSynonyms,
                    reportRequests: this.step.value.reportRequests
                },
                target: {
                    handle: this.workflowForm.value.dataSetItem.handle,
                    name: this.workflowForm.value.dataSetItem.name
                }
            }
        }).then(response => {
            if (response.modalResult) {
                let queryString = '';
                if (response.modalData.searchType === SearchType.ADVANCED) {
                    this.selectedQuery = this.xmlService.stripExtraneousParens(response.modalData.query.query ? response.modalData.query.query.trim() : response.modalData.query.trim());
                    this.setQueryDisplayValue(this.selectedQuery);
                    this.advancedQuery = response.modalData.query;
                    queryString = this.advancedQuery.xmlQuery;
                    this.isDateQuery = this.xmlService.isDateQuery(this.advancedQuery.xmlQuery);
                } else {
                    this.selectedQuery = response.modalData.query;
                    this.setQueryDisplayValue(this.selectedQuery);
                    queryString = this.xmlService.stringToXMLQuery(this.selectedQuery.trim().replace(/\u00a0/g, ' '));
                    this.advancedQuery = undefined;
                    this.isDateQuery = false;
                }
                this.step.patchValue({
                    searchType: response.modalData.searchType,
                    query: queryString,
                    includeFamilyMembers: response.modalData.includeFamilyMembers,
                    expandFields: response.modalData.expandFields,
                    includeSynonyms: response.modalData.includeSynonyms,
                    reportRequests: response.modalData.reportRequests,
                    isDateQuery: this.isDateQuery,
                    errorMessage: null
                });
                this.setOptions();
                this.step.get('query').markAsDirty();
                this.validityResult = null;
                this.input?.highlightNOW(this.selectedQueryDisplayValue, false, false, true);
                this.stepChange.emit(this.stepNumber);
            }
        });
    }

    setQueryDisplayValue(value: string) {
        this.selectedQueryDisplayValue = '';
        this.selectedQueryDisplayValue = value.length > MAX_LIMIT_OF_RENDERED_QUERY ? value.slice(0, MAX_LIMIT_OF_RENDERED_QUERY) + ' ...' : value;
    }

    clearQuery(): void {
        this.step.patchValue({ query: '' });
        this.step.get('query').markAsDirty();
        this.selectedQuery = WorkflowStepComponent.emptyQuery;
        this.selectedQueryDisplayValue = WorkflowStepComponent.emptyQuery;
        setTimeout(() => { this.input?.highlightNOW(this.selectedQueryDisplayValue, false, false, true); });
    }

    showMenu(event: MouseEvent): void {
        const targetNode: any = event?.target;
        // event button 2 is the right click button pressed
        if ((event && !this.locked && targetNode?.nodeName !== 'TEXTAREA' && targetNode?.nodeName !== 'BUTTON' && targetNode?.nodeName !== 'I') || event.button !== 2) {
            event.preventDefault();
            event.stopPropagation();
            const node = {
                contextMenuOptions: this.menuOptions,
                contextMenuType: 'workflows'
            }
            this.contextMenu?.open(event, node);
        }
    }

    iconClick(errorText: string, isWarning: boolean = false): void {
        isWarning ? this.commonService.openWarningModal(errorText) : this.commonService.openErrorModal(errorText);
    }

    getIconClass(workflowStep: any): string {
        return `fa ${workflowStep?.isWarning ? 'color-tan-07' : 'color-red-05'}`;
    }
}
